import {Component, Input, OnChanges} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';

import {StatisticsResponse} from '../../models/statistics.model';
import {
  formatCount,
  formatDate,
  locationColumns,
  StatisticsCategoryRow,
  StatisticsLocationColumn
} from '../../models/statistics-view.model';

/**
 * Eine Zeile der Matrix Art x Tiergruppe.
 * `isGroup` trennt Abgaenge von Verbleiben, die im Bestand bleiben.
 */
interface ReportMatrixRow {
  label: string;
  counts: number[];
  total: number;
  isSum: boolean;
  isGroup: boolean;
}

@Component({
  selector: 'app-statistics-report',
  standalone: false,
  templateUrl: './statistics-report.component.html',
  styleUrls: ['./statistics-report.component.scss']
})
export class StatisticsReportComponent implements OnChanges {
  @Input({required: true}) response!: StatisticsResponse;
  @Input() rows: StatisticsCategoryRow[] = [];
  @Input() startLabel = '';
  @Input() endLabel = '';
  @Input() shelterLabel = '';

  columns: StatisticsLocationColumn[] = [];
  categoryLabels: string[] = [];
  originRows: ReportMatrixRow[] = [];
  whereaboutRows: ReportMatrixRow[] = [];

  createdAt = '';

  constructor(private readonly _translocoService: TranslocoService) {
  }

  ngOnChanges(): void {
    this.columns = locationColumns(this.response);
    this.categoryLabels = this.response.categories.map(category => category.categoryLabel);
    this.createdAt = formatDate(new Date().toISOString());
    this.buildOriginRows();
    this.buildWhereaboutRows();
  }

  get periodLabel(): string {
    return `${formatDate(this.response.periodStart)} – ${formatDate(this.response.periodEnd)}`;
  }

  get footnote(): string {
    const quality = this.response.dataQuality;
    return this._translocoService.translate('statistics.report.footnote', {
      admissionsWithoutDate: formatCount(quality.admissionsWithoutDate),
      whereaboutsWithoutDate: formatCount(quality.whereaboutsWithoutDate),
      departureWithoutStock: formatCount(quality.animalsWithDepartureWhileNotInStock)
    });
  }

  /** Herkunftsarten als Zeilen, Tiergruppen als Spalten - so passt alles auf eine Seite. */
  private buildOriginRows(): void {
    const rows: ReportMatrixRow[] = this.response.total.admissionsByOriginType.map(entry => ({
      label: entry.label,
      counts: this.response.categories.map(category =>
        category.figures.admissionsByOriginType.find(item => item.type === entry.type)?.count ?? 0
      ),
      total: entry.count,
      isSum: false,
      isGroup: false
    }));

    rows.push({
      label: this._translocoService.translate('statistics.detail.originEvents'),
      counts: this.response.categories.map(category => category.figures.admissionEvents),
      total: this.response.total.admissionEvents,
      isSum: true,
      isGroup: false
    });

    this.originRows = rows;
  }

  /**
   * Verbleibsarten als Zeilen, getrennt nach Abgang und "bleibt im Bestand" -
   * die Unterscheidung ist die haeufigste Rueckfrage der Nutzerinnen.
   */
  private buildWhereaboutRows(): void {
    const all = this.response.total.whereaboutsByType;
    const toRow = (entry: typeof all[number]): ReportMatrixRow => ({
      label: entry.label,
      counts: this.response.categories.map(category =>
        category.figures.whereaboutsByType.find(item => item.type === entry.type)?.count ?? 0
      ),
      total: entry.count,
      isSum: false,
      isGroup: false
    });

    const groupRow = (key: string): ReportMatrixRow => ({
      label: this._translocoService.translate(`statistics.detail.${key}`),
      counts: [],
      total: 0,
      isSum: false,
      isGroup: true
    });

    this.whereaboutRows = [
      groupRow('departureGroup'),
      ...all.filter(entry => entry.isDeparture).map(toRow),
      {
        label: this._translocoService.translate('statistics.detail.departureEvents'),
        counts: this.response.categories.map(category => category.figures.departureEvents),
        total: this.response.total.departureEvents,
        isSum: true,
        isGroup: false
      },
      groupRow('stayGroup'),
      ...all.filter(entry => !entry.isDeparture).map(toRow)
    ];
  }
}
